import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSmartBack } from "@/lib/navigation-utils";
import {
  ArrowLeft,
  CalendarDays,
  Mail,
  MapPin,
  Pencil,
  Phone,
  User,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { PermissionGuard } from "@/components/PermissionGuard";
import { AddressLink } from "@/components/shared/AddressLink";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@akuna/ui";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { SegmentedTabs } from "@akuna/ui";
import { formatDateBR, formatLocalTimeHHmm, formatPetAge } from "@/lib/date-utils";
import { applyDocumentMask } from "@/lib/utils/document-masks";
import { supabase } from "@/integrations/supabase/client";
import { useOwner } from "@/hooks/useOwners";
import { useDocumentTypes, useLeadSources } from "@/hooks/useLookups";
import { CustomIcons } from "@/components/icons/CustomIcons";
import { WhatsAppPhoneLink } from "@/components/ui/whatsapp-phone-link";
import { AppointmentFormResponsiveDialog } from "@/components/calendar/AppointmentFormResponsiveDialog";
import { useProfile } from "@/hooks/useProfile";
import { OwnerPackagesPanel } from "@/components/packages/OwnerPackagesPanel";
import { useHasPermissionSync } from "@/hooks/usePermissions";
import { OwnerFutureTab } from "@/components/owners/OwnerFutureTab";
import { OwnerFinancialTab } from "@/components/owners/OwnerFinancialTab";
import { useEntitlements } from "@/hooks/useEntitlements";
import PatientHistoryTimeline from "@/components/patients/PatientHistoryTimeline";
import { isPetDeceased } from "@/lib/utils/pet-death";
import { MobileOwnerProfileShell, type OwnerProfileTab as MobileOwnerProfileTabType } from "@/components/owners/mobile/MobileOwnerProfileShell";
import { MobileOwnerOverviewTab } from "@/components/owners/mobile/MobileOwnerOverviewTab";
import { MobileOwnerHistoryTab } from "@/components/owners/mobile/MobileOwnerHistoryTab";
import { MobileOwnerCareTab } from "@/components/owners/mobile/MobileOwnerCareTab";
import { MobileOwnerPackagesTab } from "@/components/owners/mobile/MobileOwnerPackagesTab";
import { MobileOwnerFinancialTab } from "@/components/owners/mobile/MobileOwnerFinancialTab";
import { useDaycareHotelAccess } from "@/hooks/useDaycareHotelAccess";

type OwnerPet = {
  id: string;
  name: string;
  birth_date?: string | null;
  created_at?: string | null;
  status?: { name?: string | null } | null;
  species?: { name?: string | null } | null;
  gender?: { name?: string | null } | null;
  breed?: { name?: string | null } | null;
  photo?: { file_path?: string | null } | Array<{ file_path?: string | null }> | null;
};

type OwnerEmail = {
  id?: string | null;
  email?: string | null;
  is_primary?: boolean | null;
};

type OwnerPhone = {
  id?: string | null;
  phone_number?: string | null;
  is_primary?: boolean | null;
  is_whatsapp?: boolean | null;
};

type OwnerAddress = {
  id?: string | null;
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  neighborhood?: string | null;
  zip_code?: string | null;
  is_primary?: boolean | null;
  city?:
  | {
    name?: string | null;
    state?: { name?: string | null } | null;
  }
  | null;
};

const formatDate = (value?: string | null) => {
  return formatDateBR(value);
};

type OwnerProfileTab = "overview" | "history" | "upcoming" | "financial" | "packages";

const OWNER_PROFILE_TAB_BY_ROUTE: Record<string, OwnerProfileTab> = {
  overview: "overview",
  history: "history",
  upcoming: "upcoming",
  financial: "financial",
  packages: "packages",
  dados: "overview",
  historico: "history",
  futuro: "upcoming",
  financeiro: "financial",
  pacotes: "packages",
};

const resolvePhotoPath = (photo: OwnerPet["photo"]) => {
  if (!photo) return null;
  if (Array.isArray(photo)) return photo[0]?.file_path ?? null;
  return photo.file_path ?? null;
};

const useOwnerPets = (ownerId?: string) => {
  return useQuery({
    queryKey: ["owner-pets", ownerId],
    enabled: Boolean(ownerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pets")
        .select(
          `
            id,
            name,
            status:pet_statuses(name),
            species:species(name),
            gender:pet_genders(name),
            breed:breeds(name),
            photo:pet_photos(file_path),
            birth_date,
            created_at
          `,
        )
        .eq("owner_id", ownerId as string)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return (data ?? []) as OwnerPet[];
    },
  });
};

export default function ViewOwnerProfile() {
  const { t } = useTranslation("patients");
  const navigate = useNavigate();
  const location = useLocation();
  const { id, ownerTab } = useParams<{ id: string; ownerTab?: string }>();
  const smartBack = useSmartBack();
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);
  const routeTab = useMemo<OwnerProfileTab | null>(() => {
    if (!ownerTab) return null;
    return OWNER_PROFILE_TAB_BY_ROUTE[ownerTab] ?? null;
  }, [ownerTab]);
  const [activeTab, setActiveTab] = useState<OwnerProfileTab>(routeTab ?? "overview");
  const { data: profile } = useProfile();
  const { hasPermission: canViewPatientFinancial } = useHasPermissionSync("patient_financial", "view");
  const { hasDaycare } = useDaycareHotelAccess();
  const { data: entitlements } = useEntitlements();
  const clinicId = profile?.current_clinic_id ?? null;

  const { data: owner, isLoading: isLoadingOwner, error: ownerError } = useOwner(id ?? "");
  const {
    data: pets,
    isLoading: isLoadingPets,
    error: petsError,
  } = useOwnerPets(owner?.id);
  const { data: documentTypes } = useDocumentTypes();
  const { data: leadSources } = useLeadSources();

  const ownerAddresses = (owner?.addresses ?? []) as OwnerAddress[];

  const primaryEmail = useMemo(() => {
    const ownerEmails = (owner?.emails ?? []) as OwnerEmail[];
    return ownerEmails.find((email) => Boolean(email.is_primary)) ?? ownerEmails[0] ?? null;
  }, [owner?.emails]);

  const sortedPhones = useMemo(() => {
    const ownerPhones = (owner?.phones ?? []) as OwnerPhone[];
    const list = [...ownerPhones];
    return list.sort((a, b) => {
      const aPrimary = Boolean(a.is_primary);
      const bPrimary = Boolean(b.is_primary);
      if (aPrimary === bPrimary) return 0;
      return aPrimary ? -1 : 1;
    });
  }, [owner?.phones]);

  const petsCount = pets?.length ?? 0;
  const documentTypeName = useMemo(() => {
    if (!owner?.document_type_id) return null;
    return documentTypes?.find((type) => type.id === owner.document_type_id)?.name ?? null;
  }, [documentTypes, owner?.document_type_id]);
  const resolvedDocumentType = useMemo(() => {
    if (!owner?.document_type_id) return undefined;
    return documentTypes?.find((type) => type.id === owner.document_type_id);
  }, [documentTypes, owner?.document_type_id]);
  const formattedDocument = useMemo(() => {
    if (!owner?.document) return null;
    if (!resolvedDocumentType) return owner.document;
    return applyDocumentMask(owner.document, resolvedDocumentType);
  }, [owner?.document, resolvedDocumentType]);
  const leadSourceName = useMemo(() => {
    if (!owner?.lead_source_id) return null;
    return leadSources?.find((lead) => lead.id === owner.lead_source_id)?.name ?? null;
  }, [leadSources, owner?.lead_source_id]);
  const showGrooming = Boolean(entitlements?.activeModules?.includes("grooming"));
  const petIds = useMemo(() => (pets ?? []).map((pet) => pet.id), [pets]);
  const createAppointmentPrefill = useMemo(() => {
    if (!isCreateAppointmentOpen) return null;
    const at = new Date();
    return { date: at, time: formatLocalTimeHHmm(at) };
  }, [isCreateAppointmentOpen]);
  const resolvedActiveTab =
    canViewPatientFinancial || activeTab !== "financial" ? activeTab : "overview";
  const isOverviewTabActive = resolvedActiveTab === "overview";
  const isHistoryTabActive = resolvedActiveTab === "history";
  const isUpcomingTabActive = resolvedActiveTab === "upcoming";
  const isFinancialTabActive = resolvedActiveTab === "financial";
  const isPackagesTabActive = resolvedActiveTab === "packages";

  useEffect(() => {
    if (routeTab) {
      setActiveTab(routeTab);
      return;
    }
    if (!id) return;
    navigate(`/patients/owners/${id}/overview`, { replace: true });
  }, [id, navigate, routeTab]);

  useEffect(() => {
    if (!id) return;
    if (routeTab !== "financial") return;
    if (canViewPatientFinancial) return;

    navigate(`/patients/owners/${id}/overview`, { replace: true });
  }, [canViewPatientFinancial, id, navigate, routeTab]);

  if (!id) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("ownerProfile.backToPatients")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">{t("ownerProfile.notFound")}</h1>
          </div>
        </div>
      </PermissionGuard>
    );
  }

  if (isLoadingOwner) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("ownerProfile.backToPatients")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <Skeleton className="h-8 w-60" />
          </div>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </PermissionGuard>
    );
  }

  if (ownerError || !owner) {
    return (
      <PermissionGuard resource="patients" action="view">
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("ownerProfile.backToPatients")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-foreground">{t("ownerProfile.loadErrorTitle")}</h1>
          </div>
          <Card>
            <CardContent className="py-6">
              <p className="text-muted-foreground">
                {t("ownerProfile.loadErrorDescription")}
              </p>
              <Button className="mt-4" onClick={() => smartBack()}>
                {t("ownerProfile.backToPatients")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </PermissionGuard>
    );
  }

  // ── Mobile helpers ──
  const profileInitials = (() => {
    const name = profile?.full_name ?? "";
    const parts = name.trim().split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return "?";
  })();

  const ownerSubtitle = [
    owner.created_at ? t("ownerProfile.ownerSince", { date: formatDate(owner.created_at) }) : null,
    petsCount > 0 ? t("ownerProfile.linkedPets", { count: petsCount }) : null,
  ].filter(Boolean).join(" · ");

  const mobileOwnerTabs: { value: MobileOwnerProfileTabType; label: string }[] = [
    { value: "overview", label: t("ownerProfile.tabs.basicInfo") },
    { value: "history", label: t("ownerProfile.tabs.history") },
    { value: "upcoming", label: t("ownerProfile.tabs.appointments") },
    {
      value: "packages",
      label: t(hasDaycare ? "ownerProfile.tabs.packagesWithPlans" : "ownerProfile.tabs.packages"),
    },
    ...(canViewPatientFinancial ? [{ value: "financial" as const, label: t("ownerProfile.tabs.financial") }] : []),
  ];

  return (
    <PermissionGuard resource="patients" action="view">
      {/* ── MOBILE LAYOUT ── */}
      <div className="md:hidden h-full">
        <MobileOwnerProfileShell
          ownerName={owner.name}
          subtitleLine={ownerSubtitle}
          userInitials={profileInitials}
          activeTab={resolvedActiveTab as MobileOwnerProfileTabType}
          tabs={mobileOwnerTabs}
          onBack={() => smartBack()}
          backLabel={t("ownerProfile.backToPatients")}
          onTabChange={(tab) =>
            navigate(`/patients/owners/${owner.id}/${tab}`, { replace: true, state: location.state })
          }
          scheduleButton={
            <Button
              size="icon"
              className="fixed bottom-[calc(96px+env(safe-area-inset-bottom))] right-4 z-40 h-12 w-12 rounded-full p-0 shadow-xl shadow-primary/25 md:hidden"
              onClick={() => setIsCreateAppointmentOpen(true)}
              disabled={!clinicId}
              aria-label={t("ownerProfile.schedule")}
            >
              <CalendarDays className="h-5 w-5" />
            </Button>
          }
        >
          {resolvedActiveTab === "overview" && (
            <MobileOwnerOverviewTab
              ownerId={owner.id}
              ownerName={owner.name}
              phones={sortedPhones}
              primaryEmail={primaryEmail}
              addresses={ownerAddresses}
              documentTypeName={documentTypeName}
              formattedDocument={formattedDocument}
              birthDate={owner.birth_date ? formatDate(owner.birth_date) : null}
              profession={owner.profession ?? null}
              leadSourceName={leadSourceName}
              marketingConsent={Boolean(owner.marketing_consent)}
              pets={pets}
              isLoadingPets={isLoadingPets}
              petsError={petsError}
              editButton={
                <Button
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 rounded-full bg-background-app"
                  aria-label={t("edit", { ns: "common" })}
                  asChild
                >
                  <Link to={`/patients/owners/${owner.id}/edit`}>
                    <Pencil className="h-4 w-4" />
                  </Link>
                </Button>
              }
            />
          )}
          {resolvedActiveTab === "history" && (
            <MobileOwnerHistoryTab
              petIds={petIds}
              clinicId={clinicId}
              includeGrooming={showGrooming}
            />
          )}
          {resolvedActiveTab === "upcoming" && (
            <MobileOwnerCareTab
              ownerId={owner.id}
              petIds={petIds}
              petSummaries={(pets ?? []).map((pet) => ({
                id: pet.id,
                name: pet.name,
                status: pet.status,
              }))}
              clinicId={clinicId}
              includeGrooming={showGrooming}
            />
          )}
          {resolvedActiveTab === "packages" && (
            <MobileOwnerPackagesTab
              ownerId={owner.id}
              ownerName={owner.name}
              clinicId={clinicId}
              petIds={petIds}
              pets={(pets ?? []).map((pet) => ({ id: pet.id, name: pet.name }))}
            />
          )}
          {resolvedActiveTab === "financial" && canViewPatientFinancial && (
            <MobileOwnerFinancialTab ownerId={owner.id} petIds={petIds} />
          )}
        </MobileOwnerProfileShell>
      </div>

      {/* ── DESKTOP LAYOUT (unchanged) ── */}
      <div className="hidden md:block">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => smartBack()} aria-label={t("ownerProfile.backToPatients")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{owner.name}</h1>
              <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                <span>{t("ownerProfile.ownerSince", { date: formatDate(owner.created_at) })}</span>
                {petsCount > 0 ? (
                  <>
                    <span>•</span>
                    <span className="text-xs font-medium rounded-full bg-muted px-2 py-0.5">
                      {t("ownerProfile.linkedPets", { count: petsCount })}
                    </span>
                  </>
                ) : null}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end">
            <Button variant="outline" size="sm" asChild className="w-full md:w-auto">
              <Link to={`/patients/owners/${owner.id}/edit`}>
                <Pencil className="mr-2 h-4 w-4" />
                {t("edit", { ns: "common" })}
              </Link>
            </Button>
            <Button
              size="sm"
              className="w-full md:w-auto"
              onClick={() => setIsCreateAppointmentOpen(true)}
              disabled={!clinicId}
            >
              <CalendarDays className="mr-2 h-4 w-4" />
              {t("ownerProfile.schedule")}
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden">
          <CardHeader className="pb-3">
            <SegmentedTabs
              value={resolvedActiveTab}
              onValueChange={(value) =>
                navigate(`/patients/owners/${owner.id}/${value}`, { replace: true, state: location.state })
              }
              className="space-y-4"
              items={[
                {
                  value: "overview",
                  label: t("ownerProfile.tabs.basicInfo"),
                  content: (
              <div className="mt-4 space-y-6">
                {isOverviewTabActive ? (
                  <div className="grid gap-6 lg:grid-cols-3">
                  {/* Informações principais */}
                    <div className="lg:col-span-2 p-6 pt-0 space-y-6 px-0 md:px-6">
                      <div className="grid gap-4 sm:grid-cols-2">
                        {sortedPhones.length > 0 ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t(`ownerProfile.fields.phone`, { count: sortedPhones.length })}</span>
                            <div className="space-y-2">
                              {sortedPhones.map((phone, index) => (
                                <div key={phone.id ?? `${phone.phone_number}-${index}`} className="flex items-center gap-2">
                                  <Phone className="h-4 w-4 text-muted-foreground" />
                                  {phone.is_whatsapp ? (
                                    <WhatsAppPhoneLink
                                      phoneNumber={phone.phone_number}
                                      className="min-w-0"
                                      textClassName="truncate text-foreground hover:text-primary"
                                    />
                                  ) : (
                                    <span className="">{phone.phone_number}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {primaryEmail?.email ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t("petProfile.fields.email")}</span>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-muted-foreground" />
                              <span className="truncate">{primaryEmail.email}</span>
                            </div>
                          </div>
                        ) : null}

                        {formattedDocument ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t("ownerProfile.fields.document")}</span>
                            <span>{documentTypeName ? `${documentTypeName}: ${formattedDocument}` : formattedDocument}</span>
                          </div>
                        ) : null}

                        {owner.birth_date ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t("ownerProfile.fields.birthDate")}</span>
                            <span>{formatDate(owner.birth_date)}</span>
                          </div>
                        ) : null}

                        {owner.profession ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t("ownerProfile.fields.profession")}</span>
                            <span>{owner.profession}</span>
                          </div>
                        ) : null}

                        {leadSourceName ? (
                          <div className="space-y-1 text-sm">
                            <span className="font-semibold block text-muted-foreground">{t("ownerProfile.fields.leadSource")}</span>
                            <span>{leadSourceName}</span>
                          </div>
                        ) : null}

                        <div className="space-y-1 text-sm">
                          <span className="font-semibold block text-muted-foreground">{t("ownerProfile.fields.marketing")}</span>
                          <span>{owner.marketing_consent ? t("ownerProfile.fields.authorized") : t("ownerProfile.fields.notAuthorized")}</span>
                        </div>
                      </div>

                      <Separator />
                      {/* Endereços */}
                      {(ownerAddresses?.length ?? 0) > 0 ? (
                        <div className="rounded-lg border bg-background pb-4">
                          <div className="flex items-center justify-between gap-2 px-4 my-4">
                            <p className="text-base font-semibold text-foreground">{t("ownerProfile.fields.addresses")}</p>
                            {ownerAddresses.length > 1 ? (
                              <span className="text-xs text-muted-foreground">{ownerAddresses.length}</span>
                            ) : null}
                          </div>

                          <div className="text-sm text-muted-foreground">
                            {ownerAddresses.map((address: OwnerAddress) => {
                              const addressParts = [
                                address.street,
                                address.number,
                                address.complement,
                                address.neighborhood,
                                address.city?.name,
                                address.city?.state?.name,
                                address.zip_code,
                              ].filter(Boolean);

                              const addressText = addressParts.join(", ");
                              return (
                                <div
                                  key={address.id ?? `${address.street}-${address.number}`}
                                  className="px-4 my-2"
                                >
                                  <div className="flex items-start gap-2">
                                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                                    <div className="min-w-0">
                                      <AddressLink addressText={addressText}>
                                        <p className="break-words">
                                          {addressParts.length ? addressText : t("ownerProfile.fields.addressNotProvided")}
                                        </p>
                                      </AddressLink>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : null}
                      {/* Observações do responsável removidas */}
                    </div>

                    <Card className="h-fit !shadow-none border">
                      <CardHeader className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between flex-wrap">
                        <CardTitle className="flex items-center gap-2">
                          {t("ownerProfile.petsSection.title")}
                          {petsCount > 0 ? (
                            <span className="text-xs font-medium rounded-full bg-muted px-2 py-0.5">
                              {petsCount}
                            </span>
                          ) : null}
                        </CardTitle>
                        <Button variant="outline" size="sm" asChild className="w-full md:w-auto">
                          <Link to={`/patients/new?owner_id=${encodeURIComponent(owner.id)}`}>
                            <CustomIcons name="paws" className="h-4 w-4" />
                            {t("ownerProfile.petsSection.addPet")}
                          </Link>
                        </Button>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {petsError ? (
                          <div className="rounded-lg border p-4">
                            <p className="text-sm text-muted-foreground">
                              {t("ownerProfile.petsSection.loadError")}
                            </p>
                          </div>
                        ) : isLoadingPets ? (
                          <div className="grid gap-3 grid-cols-1">
                            <Skeleton className="h-24 w-full" />
                            <Skeleton className="h-24 w-full" />
                            <Skeleton className="h-24 w-full" />
                          </div>
                        ) : pets && pets.length > 0 ? (
                          <div className="grid gap-3 grid-cols-1">
                            {pets.map((pet) => {
                              const photoPath = resolvePhotoPath(pet.photo);
                              const ageLabel = pet.birth_date ? formatPetAge(pet.birth_date) : null;

                              return (
                                <Link
                                  key={pet.id}
                                  to={`/patients/pets/${pet.id}`}
                                  className="group rounded-lg border p-4 transition-all hover:bg-secondary hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                                >
                                  <div className="flex items-center gap-3">
                                    <Avatar className="h-12 w-12">
                                      <AvatarImage src={photoPath ?? undefined} alt={pet.name} />
                                      <AvatarFallback className="bg-muted/50 text-muted-foreground">
                                        <CustomIcons name="paws" className="h-5 w-5 text-current" />
                                      </AvatarFallback>
                                    </Avatar>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center justify-between gap-2">
                                        <p className="font-semibold text-foreground truncate">{pet.name}</p>
                                        {(pet.status?.name && isPetDeceased(pet)) ? (
                                          <Badge variant="outline">
                                            {pet.status.name}
                                          </Badge>
                                        ) : null}
                                      </div>
                                      <p className="mt-1 text-sm text-muted-foreground truncate">
                                        {[pet.species?.name, pet.breed?.name, ageLabel].filter(Boolean).join(" • ")}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="mt-3 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                                    <span>{t("ownerProfile.petsSection.viewPatient")}</span>
                                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
                              <CustomIcons name="paws" className="h-6 w-6 text-muted-foreground" />
                            </div>
                            <p className="font-medium text-foreground">{t("ownerProfile.petsSection.emptyTitle")}</p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {t("ownerProfile.petsSection.emptyDescription")}
                            </p>
                            <Button className="mt-4" asChild>
                              <Link to={`/patients/new?owner_id=${encodeURIComponent(owner.id)}`}>
                                <CustomIcons name="paws" className="mr-2 h-4 w-4 text-current" />
                                {t("ownerProfile.petsSection.newPet")}
                              </Link>
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </div>
                  ),
                },
                {
                  value: "history",
                  label: t("ownerProfile.tabs.history"),
                  content: (
              <div className="mt-4">
                {isHistoryTabActive ? (
                  <CardContent className="px-0 md:px-6">
                    <PatientHistoryTimeline
                      petIds={petIds}
                      clinicId={clinicId}
                      includeGrooming={showGrooming}
                      timeScope="past"
                      hideFutureDetails
                      showPetName
                    />
                  </CardContent>
                ) : null}
              </div>
                  ),
                },
                {
                  value: "upcoming",
                  label: t("ownerProfile.tabs.appointments"),
                  content: (
              <div className="mt-4">
                {isUpcomingTabActive ? (
                  <CardContent className="px-0 md:px-6">
                    {petIds.length > 0 ? (
                      <OwnerFutureTab
                        ownerId={owner.id}
                        petIds={petIds}
                        petSummaries={(pets ?? []).map((pet) => ({
                          id: pet.id,
                          name: pet.name,
                          status: pet.status,
                        }))}
                        clinicId={clinicId}
                        includeGrooming={showGrooming}
                      />
                    ) : (
                      <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                        {t("ownerProfile.futureEmpty")}
                      </div>
                    )}
                  </CardContent>
                ) : null}
              </div>
                  ),
                },
                ...(canViewPatientFinancial
                  ? [
                      {
                        value: "financial",
                        label: t("ownerProfile.tabs.financial"),
                        content: (
                  <div className="mt-4">
                    {isFinancialTabActive ? (
                      <PermissionGuard resource="patient_financial" action="view" showAlert={false}>
                        <CardContent className="px-0 md:px-6">
                          <OwnerFinancialTab ownerId={owner.id} petIds={petIds} />
                        </CardContent>
                      </PermissionGuard>
                    ) : null}
                  </div>
                        ),
                      },
                    ]
                  : []),
                {
                  value: "packages",
                  label: t(hasDaycare ? "ownerProfile.tabs.packagesWithPlans" : "ownerProfile.tabs.packages"),
                  content: (
              <div className="mt-4">
                {isPackagesTabActive ? (
                  <CardContent className="px-0 md:px-6">
                    {owner && clinicId ? (
                      <OwnerPackagesPanel
                        ownerId={owner.id}
                        clinicId={clinicId}
                        ownerName={owner.name}
                        petIds={petIds}
                        pets={(pets ?? []).map((pet) => ({ id: pet.id, name: pet.name }))}
                      />
                    ) : (
                      <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                        {t("ownerProfile.packagesClinicRequired")}
                      </div>
                    )}
                  </CardContent>
                ) : null}
              </div>
                  ),
                },
              ]}
            />
          </CardHeader>
        </Card>
      </div>
      </div>{/* end desktop wrapper */}

      <AppointmentFormResponsiveDialog
        open={isCreateAppointmentOpen && Boolean(clinicId && owner && createAppointmentPrefill)}
        onOpenChange={setIsCreateAppointmentOpen}
        clinicId={clinicId ?? ""}
        prefilledDate={createAppointmentPrefill?.date}
        prefilledTime={createAppointmentPrefill?.time}
        prefilledOwnerId={owner?.id}
        onSuccess={() => setIsCreateAppointmentOpen(false)}
      />
    </PermissionGuard >
  );
}
